import type { ButtonHTMLAttributes, ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'dark' | 'ghost' | 'link' | 'outline';
    size?: 'sm' | 'md' | 'lg';
    isLoading?: boolean;
    fullWidth?: boolean;
    children: ReactNode;
}

function Button({
    variant = 'primary',
    size = 'md',
    isLoading = false,
    fullWidth = false,
    className = '',
    children,
    ...rest
}: ButtonProps) {
    const base =
        'inline-flex items-center justify-center rounded-[8px] font-bold transition-all cursor-pointer duration-150 disabled:bg-[var(--primary-disabled-color)] disabled:text-[var(--primary-soft-color)] disabled:cursor-not-allowed focus:outline-none';

    const sizes: Record<NonNullable<ButtonProps['size']>, string> = {
        sm: 'px-2 py-1 text-xs',
        md: 'px-2 md:px-3 2xl:px-4 py-1.5 2xl:py-2 text-[12px] 2xl:text-[14px]',
        lg: 'px-4 py-2 text-base',
    };

    const variants: Record<NonNullable<ButtonProps['variant']>, string> = {
        primary: 'bg-[var(--primary-color)] hover:bg-[var(--primary-hover-color)] text-white',
        dark: 'bg-[var(--secondary-color)] hover:bg-[var(--secondary-hover-color)] text-white disabled:!bg-[var(--secondary-color)] disabled:!text-white disabled:opacity-70',
        ghost: 'bg-transparent text-[var(--disabled-text-color)] hover:text-[var(--secondary-color)] !p-0 font-medium',
        link: 'bg-transparent text-[var(--primary-color)] hover:underline font-medium !p-0',
        outline: 'border border-gray-300 bg-transparent hover:bg-gray-50 text-gray-700',
    };

    return (
        <button
            className={[base, sizes[size], variants[variant], fullWidth ? 'w-full' : '', className].filter(Boolean).join(' ')}
            disabled={isLoading || rest.disabled}
            {...rest}
        >
            {isLoading ? (
                <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading...
                </span>
            ) : (
                children
            )}
        </button>
    );
}

export default Button;